import {
  ConsentDocumentAuditEvent,
  ConsentDocumentAuditEventType,
  ConsentDocument,
  ConsentDocumentQuery,
  ConsentDocumentSignatory,
  ConsentDocumentStatus,
  ConsentDocumentDisplayStatus,
  ConsentDocumentActiveStage,
  ConsentSortOrder,
  ConsentCategory,
  ConsentSupportingDocument,
  ConsentSignatoryState,
  ConsentSignerRole,
  ConsentCategorySource,
  ConsentCategoryStatus,
  ConsentDocumentCategorySnapshot,
  ConsentInputValues,
  ConsentTemplate,
  ConsentTemplateInput,
  CreateConsentDocumentRequest,
  ConsentTemplateQuery,
  ConsentCategoryQuery,
  ConsentClinicalSigningMode,
  ConsentClinicalIdentityVerificationMethod,
  ConsentPhotoIdType,
  ConsentFamilyContactType,
  ConsentSigningMethod,
  ClinicalIdentityVerificationRequest,
  EMPLOYEE_CONSENT_STATEMENT,
  FAMILY_CONSENT_STATEMENT,
  FamilyIdentityVerificationRequest,
  FamilySignRequest,
  GP_CLINICAL_CONSENT_STATEMENT,
  EmployeeSignRequest,
  RESIDENT_CONSENT_STATEMENT,
  ResidentConsentRequest,
  ResidentIdentityVerificationRequest,
} from "./consentModels";
import {Role} from "@source/utils/models/adminModels";

export const CONSENT_LIST_PAGE_SIZE = 20;

/** Stored employee roles that can be allowed to sign a consent. Matching is exact. */
export const employeeSignerRoleOptions: Array<{value: string; label: string}> = [
  {value: Role.ORGANIZATION_ADMIN, label: "Organisation Manager"},
  {value: Role.HOME_ADMIN, label: "Home Manager"},
  {value: Role.FAMILY_ADMIN, label: "Family Manager"},
  {value: Role.LIFESTYLE, label: "Lifestyle"},
  {value: Role.CLINICAL_NURSE, label: "Clinical Nurse"},
  {value: Role.NURSE, label: "Nurse"},
];

export const employeeSignerRoleLabel = (role: string) =>
  employeeSignerRoleOptions.find((option) => option.value === role)?.label || role;

/** Keeps known roles in display order, then any stored values the form does not list. */
export const normalizeEmployeeSignerRoles = (roles?: string[] | null) => {
  const unique = Array.from(new Set((roles || []).map((role) => role.trim()).filter(Boolean)));
  const known = employeeSignerRoleOptions.map((option) => option.value).filter((value) => unique.includes(value));
  return [...known, ...unique.filter((value) => !known.includes(value))];
};

/** "Any Nurse" or "Any Nurse or Clinical Nurse". */
export const formatEmployeeSignerRoles = (roles?: string[] | null) => {
  const labels = normalizeEmployeeSignerRoles(roles).map((role) => employeeSignerRoleLabel(role));
  if (!labels.length) return "";
  if (labels.length === 1) return `Any ${labels[0]}`;
  return `Any ${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

export const usesRoleBasedEmployeeSigners = (
  category?: {employeeSignerRoles?: string[] | null} | null,
) => normalizeEmployeeSignerRoles(category?.employeeSignerRoles).length > 0;

/**
 * A draft keeps the roles copied when it was created. Choosing another category
 * uses that category's current roles.
 */
export const resolveDocumentEmployeeSignerRoles = (
  selected: {_id: string; employeeSignerRoles?: string[] | null} | null,
  snapshot?: {categoryId?: string; employeeSignerRoles?: string[] | null} | null,
) => {
  if (snapshot?.categoryId && (!selected || selected._id === snapshot.categoryId)) {
    return normalizeEmployeeSignerRoles(snapshot.employeeSignerRoles);
  }
  return normalizeEmployeeSignerRoles(selected?.employeeSignerRoles);
};

const roleLabels: Record<ConsentSignerRole, string> = {
  [ConsentSignerRole.EMPLOYEE]: "Employees",
  [ConsentSignerRole.GP_CLINICAL]: "GP / Clinician",
  [ConsentSignerRole.FAMILY_MEMBER]: "Family",
  [ConsentSignerRole.RESIDENT]: "Resident",
};

export const clinicalVerificationMethods: Array<{value: ConsentClinicalIdentityVerificationMethod; label: string}> = [
  {value: ConsentClinicalIdentityVerificationMethod.PERSONAL_KNOWLEDGE, label: "Personal knowledge (person is known to me)"},
  {value: ConsentClinicalIdentityVerificationMethod.PHOTO_ID, label: "Sighting a valid photo ID (e.g. Driver's Licence or Passport)"},
  {value: ConsentClinicalIdentityVerificationMethod.PROFESSIONAL_REGISTRATION, label: "Sighting professional registration (e.g. AHPRA card)"},
];

export const photoIdTypeOptions: Array<{value: ConsentPhotoIdType; label: string}> = [
  {value: ConsentPhotoIdType.DRIVERS_LICENCE, label: "Driver's Licence"},
  {value: ConsentPhotoIdType.PASSPORT, label: "Passport"},
];

export interface IdentityVerificationDetail {
  photoIdType?: ConsentPhotoIdType;
  professionalRegistration?: string;
}

export const isIdentityEvidenceComplete = (
  method?: ConsentClinicalIdentityVerificationMethod,
  detail: IdentityVerificationDetail = {},
) => {
  if (!method) return false;
  if (method === ConsentClinicalIdentityVerificationMethod.PHOTO_ID) return Boolean(detail.photoIdType);
  if (method === ConsentClinicalIdentityVerificationMethod.PROFESSIONAL_REGISTRATION) {
    return Boolean(detail.professionalRegistration?.trim());
  }
  return true;
};

const identityVerificationStatement = (
  signerName: string,
  verificationMethod: ConsentClinicalIdentityVerificationMethod,
  detail: IdentityVerificationDetail = {},
) => {
  const label = clinicalVerificationMethods.find((method) => method.value === verificationMethod)?.label || "";
  const base = `I confirm I am with ${signerName} and have verified their identity in person by: ${label}`;
  if (verificationMethod === ConsentClinicalIdentityVerificationMethod.PHOTO_ID && detail.photoIdType) {
    const photoLabel = photoIdTypeOptions.find((option) => option.value === detail.photoIdType)?.label;
    return photoLabel ? `${base}: ${photoLabel}` : base;
  }
  const registration = detail.professionalRegistration?.trim();
  if (verificationMethod === ConsentClinicalIdentityVerificationMethod.PROFESSIONAL_REGISTRATION && registration) {
    return `${base}: ${registration}`;
  }
  return base;
};

export const formatIdentityVerificationMethod = (verification?: {
  verificationMethod?: ConsentClinicalIdentityVerificationMethod;
  photoIdType?: ConsentPhotoIdType | null;
  professionalRegistration?: string | null;
} | null) => {
  if (!verification?.verificationMethod) return "";
  return identityVerificationStatement("them", verification.verificationMethod, {
    photoIdType: verification.photoIdType || undefined,
    professionalRegistration: verification.professionalRegistration || undefined,
  }).replace("I confirm I am with them and have verified their identity in person by: ", "");
};

export const familyContactTypes: Array<{value: ConsentFamilyContactType; label: string}> = [
  {value: ConsentFamilyContactType.PRIMARY, label: "Primary"},
  {value: ConsentFamilyContactType.SECONDARY, label: "Secondary"},
  {value: ConsentFamilyContactType.OTHER, label: "Other"},
];

export type ConsentStatusTone = "neutral" | "info" | "success" | "danger";

export interface ConsentStatusPresentation {
  label: string;
  tone: ConsentStatusTone;
}

export const getConsentStatusPresentation = (
  status: ConsentDocumentStatus,
  activeStages?: ConsentDocumentActiveStage[],
  clinicalSigningMode?: ConsentClinicalSigningMode,
  displayStatus?: ConsentDocumentDisplayStatus,
  familySigningMode?: ConsentClinicalSigningMode,
): ConsentStatusPresentation => {
  if (displayStatus === ConsentDocumentDisplayStatus.WITHDRAWAL_REQUESTED) return {label: "Withdrawal requested", tone: "danger"};
  if (displayStatus === ConsentDocumentDisplayStatus.WITHDRAWN || status === ConsentDocumentStatus.WITHDRAWN) return {label: "Withdrawn", tone: "danger"};
  if (displayStatus === ConsentDocumentDisplayStatus.EXPIRED) return {label: "Expired", tone: "danger"};
  if (displayStatus === ConsentDocumentDisplayStatus.REVIEW_OVERDUE) return {label: "Review Overdue", tone: "danger"};
  if (status === ConsentDocumentStatus.DRAFT) return {label: "Draft", tone: "neutral"};
  if (status === ConsentDocumentStatus.COMPLETED) return {label: "Completed", tone: "success"};
  if (status === ConsentDocumentStatus.DECLINED) return {label: "Declined", tone: "danger"};
  if (status === ConsentDocumentStatus.CANCELLED || displayStatus === ConsentDocumentDisplayStatus.CANCELLED) return {label: "Cancelled", tone: "neutral"};
  if (!Array.isArray(activeStages)) {
    return {label: "Awaiting signatures", tone: "info"};
  }
  if (activeStages.length === 0) {
    return {label: "Finalizing", tone: "info"};
  }

  const stageLabels: Record<ConsentDocumentActiveStage, string> = {
    [ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES]: roleLabels[ConsentSignerRole.EMPLOYEE],
    [ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT]: roleLabels[ConsentSignerRole.FAMILY_MEMBER],
    [ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION]: roleLabels[ConsentSignerRole.RESIDENT],
    [ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE]: roleLabels[ConsentSignerRole.GP_CLINICAL],
  };
  const labels = activeStages.map((stage) => stageLabels[stage]).filter(Boolean);
  if (labels.length === 1) {
    if (activeStages[0] === ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES) {
      return {label: "Awaiting employee signatures", tone: "info"};
    }
    if (activeStages[0] === ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT) {
      return {
        label: familySigningMode === ConsentClinicalSigningMode.ON_SITE
          ? "Ready for family member on-site signing"
          : "Awaiting family signatures",
        tone: "info",
      };
    }
    if (activeStages[0] === ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION) {
      return {label: "Ready for resident presentation", tone: "info"};
    }
    return {
      label: clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE
        ? "Ready for GP / Clinician on-site signing"
        : "Awaiting GP / Clinician signature",
      tone: "info",
    };
  }
  return {label: `Awaiting signatures: ${labels.join(", ")}`, tone: "info"};
};

export const getCurrentSignerPresentation = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "clinicalSigningMode" | "familySigningMode" | "displayStatus">,
  signerId: string,
): ConsentStatusPresentation => {
  const signer = document.signatories?.find((item) => item.id === signerId);
  if (
    signer?.signed &&
    document.status !== ConsentDocumentStatus.COMPLETED &&
    document.status !== ConsentDocumentStatus.DECLINED &&
    document.status !== ConsentDocumentStatus.WITHDRAWN &&
    document.status !== ConsentDocumentStatus.CANCELLED
  ) {
    return {label: "You signed · Waiting for other required signers", tone: "info"};
  }
  return getConsentStatusPresentation(
    document.status,
    document.activeStages,
    document.clinicalSigningMode,
    document.displayStatus,
    document.familySigningMode,
  );
};

export interface ConsentRoleProgress {
  role: ConsentSignerRole;
  label: string;
  total: number;
  signed: number;
  active: number;
  waiting: number;
}

export const getConsentRoleProgress = (
  signatories?: ConsentDocumentSignatory[],
): ConsentRoleProgress[] => {
  if (!signatories?.length) return [];

  return [
    ConsentSignerRole.EMPLOYEE,
    ConsentSignerRole.GP_CLINICAL,
    ConsentSignerRole.FAMILY_MEMBER,
    ConsentSignerRole.RESIDENT,
  ].reduce<ConsentRoleProgress[]>((groups, role) => {
    const roleSignatories = signatories.filter((signatory) => signatory.role === role);
    if (!roleSignatories.length) return groups;
    groups.push({
      role,
      label: roleLabels[role],
      total: roleSignatories.length,
      signed: roleSignatories.filter((signatory) => signatory.signed).length,
      active: roleSignatories.filter((signatory) => signatory.state === ConsentSignatoryState.ACTIVE).length,
      waiting: roleSignatories.filter((signatory) => signatory.state === ConsentSignatoryState.WAITING).length,
    });
    return groups;
  }, []);
};

const auditEventLabels: Partial<Record<ConsentDocumentAuditEventType, string>> = {
  [ConsentDocumentAuditEventType.CREATED]: "Document created",
  [ConsentDocumentAuditEventType.DRAFT_UPDATED]: "Draft updated",
  [ConsentDocumentAuditEventType.PDF_ATTACHED]: "PDF attached",
  [ConsentDocumentAuditEventType.SENT]: "Document sent",
  [ConsentDocumentAuditEventType.REMINDER_SENT]: "Reminder sent",
  [ConsentDocumentAuditEventType.EMPLOYEE_SIGNED]: "Employee signed",
  [ConsentDocumentAuditEventType.FAMILY_CONSENTED]: "Family consent recorded",
  [ConsentDocumentAuditEventType.FAMILY_DECLINED]: "Family declined",
  [ConsentDocumentAuditEventType.IDENTITY_VERIFIED]: "Resident identity verified",
  [ConsentDocumentAuditEventType.RESIDENT_CONSENTED]: "Resident consent recorded",
  [ConsentDocumentAuditEventType.RESIDENT_DECLINED]: "Resident declined",
  [ConsentDocumentAuditEventType.CLINICAL_IDENTITY_VERIFIED]: "GP / Clinician identity verified",
  [ConsentDocumentAuditEventType.CLINICAL_SIGNED]: "GP / Clinician signed",
  [ConsentDocumentAuditEventType.CLINICAL_DECLINED]: "GP / Clinician declined",
  [ConsentDocumentAuditEventType.FAMILY_IDENTITY_VERIFIED]: "Family member identity verified",
  [ConsentDocumentAuditEventType.COMPLETED]: "Document completed",
  [ConsentDocumentAuditEventType.FINAL_PDF_VIEWED]: "Final PDF viewed",
  [ConsentDocumentAuditEventType.WITHDRAWAL_REQUESTED]: "Withdrawal requested by family member",
  [ConsentDocumentAuditEventType.WITHDRAWAL_REQUEST_CLOSED]: "Withdrawal request closed",
  [ConsentDocumentAuditEventType.WITHDRAWAL_REMINDER_SENT]: "Withdrawal reminder sent",
  [ConsentDocumentAuditEventType.WITHDRAWN]: "Consent withdrawn",
  [ConsentDocumentAuditEventType.CANCELLED]: "Document cancelled",
  [ConsentDocumentAuditEventType.EXPIRED]: "Consent expired",
  [ConsentDocumentAuditEventType.REVIEW_OVERDUE]: "Consent review overdue",
};

/** Produces readable evidence text while allowing newly-added backend events through. */
export const getConsentAuditEventLabel = (
  event: Pick<ConsentDocumentAuditEvent, "type"> & Partial<Pick<ConsentDocumentAuditEvent, "recipientName" | "daysBefore">>,
): string => {
  if (event.type === ConsentDocumentAuditEventType.FINAL_PDF_EMAILED) {
    const recipient = event.recipientName?.trim();
    return recipient
      ? `Completed PDF emailed to ${recipient} (family member requested a copy from the app)`
      : "Completed PDF emailed (family member requested a copy from the app)";
  }
  if (event.type === ConsentDocumentAuditEventType.EXPIRY_REMINDER_SENT) {
    return typeof event.daysBefore === "number"
      ? `Expiry reminder sent (${event.daysBefore} days before)`
      : "Expiry reminder sent";
  }
  if (event.type === ConsentDocumentAuditEventType.REVIEW_REMINDER_SENT) {
    return typeof event.daysBefore === "number"
      ? `Review reminder sent (${event.daysBefore} days before)`
      : "Review reminder sent";
  }
  return auditEventLabels[event.type] || event.type.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getConsentSignerRoleLabel = (role?: ConsentSignerRole): string => {
  if (role === ConsentSignerRole.EMPLOYEE) return "Employee";
  if (role === ConsentSignerRole.GP_CLINICAL) return "GP / Clinician";
  if (role === ConsentSignerRole.FAMILY_MEMBER) return "Family member";
  if (role === ConsentSignerRole.RESIDENT) return "Resident";
  return "Signer";
};

export const getConsentSignatoryName = (
  signatory: Pick<ConsentDocumentSignatory, "name" | "role" | "signingMode"> & {id?: string | null},
  employeeSignerRoles?: string[] | null,
) => {
  const name = signatory.name?.trim();
  if (!name && signatory.role === ConsentSignerRole.EMPLOYEE && !signatory.id) {
    const roleLabel = formatEmployeeSignerRoles(employeeSignerRoles);
    if (roleLabel) return roleLabel;
  }
  if (!name && signatory.role === ConsentSignerRole.GP_CLINICAL) return "GP / Clinician (on-site)";
  if (!name && signatory.role === ConsentSignerRole.FAMILY_MEMBER && signatory.signingMode === ConsentClinicalSigningMode.ON_SITE) {
    return "Family member (on-site)";
  }
  if (!name) return "Signer unavailable";
  if (signatory.role === ConsentSignerRole.FAMILY_MEMBER) return name;
  if (signatory.signingMode === ConsentClinicalSigningMode.ON_SITE) return `${name} · On site`;
  if (signatory.signingMode === ConsentClinicalSigningMode.BRENNA_USER) return `${name} · Brenna employee`;
  return name;
};

export const getFamilyContactTypeLabel = (value?: string) => {
  const match = familyContactTypes.find((option) => option.value === value?.trim().toLowerCase());
  return match?.label || value || "";
};

export const getConsentSigningMethodLabel = (method?: string) =>
  method === ConsentSigningMethod.FAMILY_ON_SITE ? "Family member on-site" : method || "";

export const createEmployeeSignRequest = (asClinicalSigner = false): EmployeeSignRequest => ({
  consentConfirmed: true,
  acceptanceStatement: asClinicalSigner ? GP_CLINICAL_CONSENT_STATEMENT : EMPLOYEE_CONSENT_STATEMENT,
});

export const employeeSignPath = (documentId: string) =>
  `/home/consent-documents/sign/${documentId}`;

export const consentTemplateBuilderPath = (templateId: string) =>
  `/home/consent-documents/templates/${templateId}`;

export const createResidentConsentRequest = (): ResidentConsentRequest => ({
  consentConfirmed: true,
  acceptanceStatement: RESIDENT_CONSENT_STATEMENT,
});

export const createResidentIdentityRequest = (
  residentFullName: string,
): ResidentIdentityVerificationRequest => ({
  identityVerified: true,
  identityVerificationStatement:
    `I confirm I am with ${residentFullName.trim()} and have verified their identity in person.`,
});

const identityEvidenceFields = (
  verificationMethod: ConsentClinicalIdentityVerificationMethod,
  detail: IdentityVerificationDetail,
) => ({
  ...(verificationMethod === ConsentClinicalIdentityVerificationMethod.PHOTO_ID && detail.photoIdType
    ? {photoIdType: detail.photoIdType}
    : {}),
  ...(verificationMethod === ConsentClinicalIdentityVerificationMethod.PROFESSIONAL_REGISTRATION && detail.professionalRegistration?.trim()
    ? {professionalRegistration: detail.professionalRegistration.trim()}
    : {}),
});

export const createClinicalIdentityRequest = (
  signerName: string,
  verificationMethod: ConsentClinicalIdentityVerificationMethod,
  detail: IdentityVerificationDetail = {},
): ClinicalIdentityVerificationRequest => {
  const name = signerName.trim();
  return {
    signerName: name,
    verificationMethod,
    ...identityEvidenceFields(verificationMethod, detail),
    identityVerified: true,
    identityVerificationStatement: identityVerificationStatement(name, verificationMethod, detail),
  };
};

export const createFamilyIdentityRequest = (
  firstName: string,
  lastName: string,
  relationship: string,
  contactType: ConsentFamilyContactType,
  verificationMethod: ConsentClinicalIdentityVerificationMethod,
  detail: IdentityVerificationDetail = {},
): FamilyIdentityVerificationRequest => {
  const first = firstName.trim();
  const last = lastName.trim();
  return {
    firstName: first,
    lastName: last,
    relationship: relationship.trim(),
    contactType,
    verificationMethod,
    ...identityEvidenceFields(verificationMethod, detail),
    identityVerified: true,
    identityVerificationStatement: identityVerificationStatement(`${first} ${last}`, verificationMethod, detail),
  };
};

export const createFamilySignRequest = (familyMemberInputValues?: ConsentInputValues): FamilySignRequest => ({
  consentConfirmed: true,
  acceptanceStatement: FAMILY_CONSENT_STATEMENT,
  ...(familyMemberInputValues && Object.keys(familyMemberInputValues).length ? {familyMemberInputValues} : {}),
});

export type ConsentListQuery = ConsentCategoryQuery | ConsentTemplateQuery | ConsentDocumentQuery;

/**
 * UI-only history selections are namespaced so lifecycle and active-stage
 * values cannot be confused when constructing the API query.
 */
export type ConsentHistoryStatusFilter =
  | "all"
  | "lifecycle:draft"
  | "lifecycle:awaiting_signatures"
  | "stage:awaiting_employee_signatures"
  | "stage:awaiting_family_consent"
  | "stage:ready_for_resident_presentation"
  | "stage:awaiting_clinical_signature"
  | "lifecycle:completed"
  | "display:expired"
  | "display:review_overdue"
  | "lifecycle:declined"
  | "lifecycle:withdrawn"
  | "lifecycle:cancelled"
  | "withdrawal:requested";

export interface ConsentHistoryFilters {
  statusFilter?: ConsentHistoryStatusFilter;
  residentId?: string;
  categoryId?: string;
  search?: string;
  dateRange?: [Date, Date] | [Date] | null;
  sort: ConsentSortOrder;
}

export const getConsentDocumentHistoryStatusQuery = (
  statusFilter?: ConsentHistoryStatusFilter,
): Pick<ConsentDocumentQuery, "status" | "activeStage" | "withdrawalRequested" | "expired" | "reviewOverdue"> => {
  switch (statusFilter) {
    case "lifecycle:draft":
      return {status: ConsentDocumentStatus.DRAFT};
    case "lifecycle:awaiting_signatures":
      return {status: ConsentDocumentStatus.AWAITING_SIGNATURES};
    case "stage:awaiting_employee_signatures":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.AWAITING_EMPLOYEE_SIGNATURES,
      };
    case "stage:awaiting_family_consent":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT,
      };
    case "stage:ready_for_resident_presentation":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION,
      };
    case "stage:awaiting_clinical_signature":
      return {
        status: ConsentDocumentStatus.AWAITING_SIGNATURES,
        activeStage: ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE,
      };
    case "lifecycle:completed":
      return {status: ConsentDocumentStatus.COMPLETED};
    case "display:expired":
      return {expired: true};
    case "display:review_overdue":
      return {reviewOverdue: true};
    case "lifecycle:declined":
      return {status: ConsentDocumentStatus.DECLINED};
    case "lifecycle:withdrawn":
      return {status: ConsentDocumentStatus.WITHDRAWN};
    case "lifecycle:cancelled":
      return {status: ConsentDocumentStatus.CANCELLED};
    case "withdrawal:requested":
      return {withdrawalRequested: true};
    case "all":
    default:
      return {};
  }
};

/** Builds the list request without sending empty or unsupported filter values. */
export const createConsentDocumentHistoryQuery = ({
  statusFilter,
  residentId,
  categoryId,
  search,
  dateRange,
  sort,
}: ConsentHistoryFilters): ConsentDocumentQuery => ({
  ...getConsentDocumentHistoryStatusQuery(statusFilter),
  ...(residentId ? {residentId} : {}),
  ...(categoryId ? {categoryId} : {}),
  ...(search?.trim() ? {search: search.trim()} : {}),
  ...(dateRange?.[0] ? {startDate: new Date(new Date(dateRange[0]).setHours(0, 0, 0, 0)).toISOString()} : {}),
  ...(dateRange?.[1] ? {endDate: new Date(new Date(dateRange[1]).setHours(23, 59, 59, 999)).toISOString()} : {}),
  sort,
});

export const consentDocumentDetailPath = (documentId: string) =>
  `/home/consent-documents/documents/${documentId}`;

/** Formats the populated `createdBy` sender returned on consent list/detail DTOs. */
export const formatConsentSender = (sender?: Pick<ConsentDocument["createdBy"], "firstName" | "lastName" | "account">) => {
  const name = [sender?.firstName, sender?.lastName].filter(Boolean).join(" ").trim();
  const position = sender?.account?.position?.trim();
  if (!name) return "";
  return `Sent by ${name}${position ? ` (${position})` : ""}`;
};

export const consentDocumentEditPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/edit`;

export const residentPresentationPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/presentation`;

export const clinicalPresentationPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/clinical-presentation`;

export const familyPresentationPath = (residentId: string, documentId: string) =>
  `/home/residents/${residentId}/consent-documents/${documentId}/family-presentation`;

/** Whether the resident can be presented this document at its current server-computed stage. */
export const canPresentResidentNow = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "categorySnapshot" | "signatories" | "signatures">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION)
  ) return false;
  const residentAlreadySigned = document.signatures?.some((signature) =>
    signature.signerType === ConsentSignerRole.RESIDENT,
  );
  if (document.signatories !== undefined) {
    const residentSignatory = document.signatories.find((signatory) =>
      signatory.role === ConsentSignerRole.RESIDENT,
    );
    return Boolean(residentSignatory
        && !residentSignatory.signed
        && residentSignatory.state === ConsentSignatoryState.ACTIVE);
  }
  // Category card/list records are expected to include computed signatories;
  // only category-less legacy records may use this evidence-only fallback.
  return !document.categorySnapshot && !residentAlreadySigned;
};

/**
 * Validates the small amount of stage data a resident-presentation response may
 * expose. This is only for the dedicated resident-presentation endpoint: its
 * successful response is the server's authorization that presentation is
 * allowed, even when the serializer omits computed signatories.
 */
export const canUseResidentPresentationResponse = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures">,
) => {
  const residentAlreadySigned = document.signatures?.some((signature) =>
    signature.signerType === ConsentSignerRole.RESIDENT,
  );
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION)
  ) {
    return false;
  }
  if (document.signatories !== undefined) {
    const residentSignatory = document.signatories.find((signatory) =>
      signatory.role === ConsentSignerRole.RESIDENT,
    );
    return Boolean(
      residentSignatory
      && !residentSignatory.signed
      && residentSignatory.state === ConsentSignatoryState.ACTIVE,
    );
  }
  return !residentAlreadySigned;
};

/** Only the returned send response determines whether resident presentation is active. */
export const isResidentImmediatelyActiveAfterSend = (
  document: Pick<ConsentDocument, "status" | "activeStages">,
) => document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
  && Array.isArray(document.activeStages)
  && document.activeStages.includes(ConsentDocumentActiveStage.READY_FOR_RESIDENT_PRESENTATION);

export const canPresentClinicalNow = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures" | "clinicalSigningMode">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || document.clinicalSigningMode !== ConsentClinicalSigningMode.ON_SITE
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE)
  ) return false;
  if (document.signatories !== undefined) {
    const signatory = document.signatories.find((item) => item.role === ConsentSignerRole.GP_CLINICAL);
    return Boolean(signatory && !signatory.signed && signatory.state === ConsentSignatoryState.ACTIVE);
  }
  return !document.signatures?.some((signature) => signature.signerType === ConsentSignerRole.GP_CLINICAL);
};

/** Successful clinical-presentation responses authorize the flow even when signatories are omitted. */
export const canUseClinicalPresentationResponse = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures" | "clinicalSigningMode">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE)
    || (document.clinicalSigningMode && document.clinicalSigningMode !== ConsentClinicalSigningMode.ON_SITE)
  ) return false;
  if (document.signatories !== undefined) {
    const signatory = document.signatories.find((item) => item.role === ConsentSignerRole.GP_CLINICAL);
    return Boolean(signatory && !signatory.signed && signatory.state === ConsentSignatoryState.ACTIVE);
  }
  return !document.signatures?.some((signature) => signature.signerType === ConsentSignerRole.GP_CLINICAL);
};

export const isClinicalOnSiteImmediatelyActiveAfterSend = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "clinicalSigningMode">,
) => document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
  && document.clinicalSigningMode === ConsentClinicalSigningMode.ON_SITE
  && Array.isArray(document.activeStages)
  && document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE);

export const canPresentFamilyNow = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures" | "familySigningMode">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || document.familySigningMode !== ConsentClinicalSigningMode.ON_SITE
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT)
  ) return false;
  if (document.signatories !== undefined) {
    const signatory = document.signatories.find((item) => item.role === ConsentSignerRole.FAMILY_MEMBER);
    return Boolean(signatory && !signatory.signed && signatory.state === ConsentSignatoryState.ACTIVE);
  }
  return !document.signatures?.some((signature) => signature.signerType === ConsentSignerRole.FAMILY_MEMBER);
};

/** Successful family-presentation responses authorize the flow even when signatories are omitted. */
export const canUseFamilyPresentationResponse = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "signatories" | "signatures" | "familySigningMode">,
) => {
  if (
    document.status !== ConsentDocumentStatus.AWAITING_SIGNATURES
    || !Array.isArray(document.activeStages)
    || !document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT)
    || (document.familySigningMode && document.familySigningMode !== ConsentClinicalSigningMode.ON_SITE)
  ) return false;
  if (document.signatories !== undefined) {
    const signatory = document.signatories.find((item) => item.role === ConsentSignerRole.FAMILY_MEMBER);
    return Boolean(signatory && !signatory.signed && signatory.state === ConsentSignatoryState.ACTIVE);
  }
  return !document.signatures?.some((signature) => signature.signerType === ConsentSignerRole.FAMILY_MEMBER);
};

export const isFamilyOnSiteImmediatelyActiveAfterSend = (
  document: Pick<ConsentDocument, "status" | "activeStages" | "familySigningMode">,
) => document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
  && document.familySigningMode === ConsentClinicalSigningMode.ON_SITE
  && Array.isArray(document.activeStages)
  && document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_FAMILY_CONSENT);

export const getResidentConsentResultCopy = (status: ConsentDocumentStatus) =>
  status === ConsentDocumentStatus.COMPLETED
    ? "All required consent steps are complete."
    : "Consent was recorded and this document is waiting for the remaining required signers.";

export interface ConsentStepsValues {
  categoryId: string;
  employeeSignerIds: string[];
  familyMemberIds: string[];
  familyFieldCompleterId: string;
  employeeInputValues: ConsentInputValues;
  clinicalSigningMode: ConsentClinicalSigningMode | "";
  clinicalSignerId: string;
  familySigningMode: ConsentClinicalSigningMode | "";
}

export const MAX_CONSENT_PDF_BYTES = 25 * 1024 * 1024;

/** Reduces a response Content-Type to the supported preview media type. */
export const normalizeConsentPreviewContentType = (contentType?: string): string => {
  const mediaType = contentType?.split(";", 1)[0]?.trim().toLowerCase();
  if (mediaType === "text/html") return "text/html; charset=utf-8";
  if (mediaType === "application/pdf") return "application/pdf";
  return mediaType || "";
};

export const getConsentPreviewErrorDescription = (status?: number): string | undefined => {
  if (status === 403) return "You are not authorized to preview this document at the current stage.";
  if (status === 404) return "The consent document or its confirmed source is no longer available.";
  if (status === 409) return "This document cannot be previewed in its current workflow state. Completed documents use the final PDF.";
  return undefined;
};

export interface ConsentPdfFileMetadata {
  name: string;
  size: number;
  type: string;
}

/** Validates local metadata before requesting an upload target. */
export const validateConsentPdfFile = (file: ConsentPdfFileMetadata | null): string => {
  if (!file) return "Choose a PDF file.";
  if (file.size <= 0) return "The PDF file cannot be empty.";
  if (file.size > MAX_CONSENT_PDF_BYTES) return "The PDF must be 25 MiB or smaller.";
  if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
    return "Choose a PDF file.";
  }
  return "";
};

/** Validates a single local PDF selection before an upload target is requested. */
export const validateConsentPdfFiles = (files: ConsentPdfFileMetadata[]): string => {
  if (files.length !== 1) return "Choose one PDF file.";
  return validateConsentPdfFile(files[0]);
};

export const MAX_CONSENT_SUPPORTING_DOCUMENTS = 10;
export const MAX_SUPPORTING_DOCUMENT_NAME_LENGTH = 255;

/** Formats a supporting-document size from its byte length. */
export const formatConsentFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return "Size unavailable";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const value = bytes < 1024 * 1024 ? bytes / 1024 : bytes / (1024 * 1024);
  const unit = bytes < 1024 * 1024 ? "KB" : "MB";
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} ${unit}`;
};

/**
 * Keeps files that can be uploaded now. Extra files past the category limit are reported once
 * and left out of the accepted list.
 */
export const selectSupportingDocumentFiles = <T extends ConsentPdfFileMetadata>(
  files: T[],
  currentCount: number,
): {accepted: T[]; errors: string[]} => {
  const remaining = MAX_CONSENT_SUPPORTING_DOCUMENTS - Math.max(0, currentCount);
  if (remaining <= 0) {
    return {accepted: [], errors: ["A category can have at most 10 supporting documents."]};
  }
  const accepted: T[] = [];
  const errors: string[] = [];
  let skippedForLimit = false;
  for (const file of files) {
    const name = file.name?.trim() || "Selected file";
    if ((file.name || "").length > MAX_SUPPORTING_DOCUMENT_NAME_LENGTH) {
      errors.push(`${name}: File name must be 255 characters or fewer.`);
      continue;
    }
    const invalid = validateConsentPdfFile(file);
    if (invalid) {
      errors.push(`${name}: ${invalid}`);
      continue;
    }
    if (accepted.length >= remaining) {
      skippedForLimit = true;
      continue;
    }
    accepted.push(file);
  }
  if (skippedForLimit) errors.push("A category can have at most 10 supporting documents.");
  return {accepted, errors};
};

/** Unions category snapshots returned by overlapping confirms. */
export const mergeSupportingDocuments = (
  current: ConsentSupportingDocument[],
  incoming?: ConsentSupportingDocument[],
): ConsentSupportingDocument[] => {
  const merged = new Map(current.map((document) => [document._id, document]));
  for (const document of incoming ?? []) merged.set(document._id, document);
  const documents: ConsentSupportingDocument[] = [];
  merged.forEach((document) => documents.push(document));
  return documents;
};

const supportingDocumentErrorMessages: Record<string, string> = {
  consent_category_supporting_document_invalid: "The file must be a PDF of up to 25 MB.",
  consent_category_supporting_document_limit: "A category can have at most 10 supporting documents.",
  consent_category_supporting_document_not_found: "This document was already removed.",
  consent_category_not_found: "This Consent Category is no longer available.",
  not_enough_permissions: "You do not have permission to manage Consent Categories.",
  consent_document_missing_organization_bucket: "Consent storage isn't configured for this organization.",
};

export const getConsentRequestStringCode = (error: unknown): string =>
  typeof error === "object" && error !== null && "stringCode" in error
  && typeof (error as {stringCode?: unknown}).stringCode === "string"
    ? (error as {stringCode: string}).stringCode
    : "";

export const getSupportingDocumentErrorMessage = (error: unknown, fallback: string): string => {
  const known = supportingDocumentErrorMessages[getConsentRequestStringCode(error)];
  if (known) return known;
  return error instanceof Error && error.message ? error.message : fallback;
};

export const shouldRefreshSupportingDocuments = (error: unknown): boolean => {
  const code = getConsentRequestStringCode(error);
  return code === "consent_category_supporting_document_limit"
    || code === "consent_category_supporting_document_not_found"
    || code === "consent_category_not_found";
};

export const isConsentPdfHeader = (bytes: Uint8Array) =>
  bytes.length >= 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44
  && bytes[3] === 0x46 && bytes[4] === 0x2d;

export const hasValidConfirmedConsentPdf = (
  document: Pick<ConsentDocument, "pdf" | "categoryId" | "categorySnapshot" | "resident"> | null,
  categoryId: string,
  residentId: string,
) => Boolean(document?.pdf?.originalFileName && categoryId && residentId
  && document.resident?._id === residentId
  && (document.categoryId || document.categorySnapshot?.categoryId) === categoryId);

export const emptyConsentStepsValues = (): ConsentStepsValues => ({
  categoryId: "",
  employeeSignerIds: [],
  familyMemberIds: [],
  familyFieldCompleterId: "",
  employeeInputValues: {},
  clinicalSigningMode: ConsentClinicalSigningMode.BRENNA_USER,
  clinicalSignerId: "",
  familySigningMode: ConsentClinicalSigningMode.BRENNA_USER,
});

export const normalizeConsentIds = (ids: string[]) =>
  Array.from(new Set(ids.filter(Boolean)));

const normalizeConsentDescriptor = (value?: string) =>
  value?.trim().replace(/\s+/g, " ").toLocaleLowerCase() || "";

/** True only when a card's category adds information beyond its title. */
export const shouldDisplayConsentCategoryName = (title?: string, categoryName?: string) =>
  Boolean(categoryName) && normalizeConsentDescriptor(title) !== normalizeConsentDescriptor(categoryName);

/** Step 1 only confirms that the selected category is still an active, fetched category. */
export const validateConsentCategorySelection = (
  category: Pick<ConsentCategory, "status"> | null,
  categoryUnavailable: boolean,
): string => {
  if (category?.status === ConsentCategoryStatus.ACTIVE) return "";
  if (categoryUnavailable || category?.status === ConsentCategoryStatus.ARCHIVED) {
    return "This Consent Category is no longer active. Select another active category.";
  }
  return "Wait for active Consent Categories to finish loading.";
};

/** Orders input definitions by their first {{actor.id}} placeholder in the document HTML. */
export const sortConsentInputsByDocument = <T extends Pick<ConsentTemplateInput, "id">>(
  inputs: T[],
  html: string | undefined,
  actor: "employee" | "family",
): T[] => {
  const order = new Map<string, number>();
  if (html) {
    const pattern = new RegExp(`\\{\\{\\s*${actor}\\.([A-Za-z][A-Za-z0-9_-]*)\\s*\\}\\}`, "g");
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(html))) {
      if (!order.has(match[1])) order.set(match[1], order.size);
    }
  }
  return inputs
    .map((input, index) => ({input, index}))
    .sort((left, right) => {
      const leftRank = order.get(left.input.id) ?? order.size + left.index;
      const rightRank = order.get(right.input.id) ?? order.size + right.index;
      return leftRank - rightRank;
    })
    .map(({input}) => input);
};

/** Omits blank text values, while preserving supported non-text input values. */
export const cleanFamilyMemberInputValues = (
  inputs: ConsentTemplate["familyMemberInputs"],
  values: ConsentInputValues,
): ConsentInputValues => cleanEmployeeInputValues({employeeInputs: inputs}, values);

export const cleanEmployeeInputValues = (
  template: Pick<ConsentTemplate, "employeeInputs">,
  values: ConsentInputValues,
): ConsentInputValues => template.employeeInputs.reduce<ConsentInputValues>((result, input) => {
  const value = values[input.id];
  if (input.type === "text" || input.type === "date" || input.type === "datetime") {
    if (typeof value === "string" && value.trim()) result[input.id] = value.trim();
  } else if (value !== undefined) {
    result[input.id] = value;
  }
  return result;
}, {});

export const createConsentStepsPayload = (
  residentId: string,
  category: Pick<ConsentCategory, "_id" | "requiredSignerRoles" | "source" | "employeeSignerRoles"> | Pick<ConsentDocumentCategorySnapshot, "categoryId" | "requiredSignerRoles" | "source" | "employeeSignerRoles">,
  template: Pick<ConsentTemplate, "employeeInputs"> | null,
  values: ConsentStepsValues,
): CreateConsentDocumentRequest => {
  const categoryId = "categoryId" in category ? category.categoryId : category._id;
  const roles = category.requiredSignerRoles;
  const isTemplate = category.source === ConsentCategorySource.TEMPLATE;
  const roleBasedEmployees = usesRoleBasedEmployeeSigners(category);
  const employees = normalizeConsentIds(values.employeeSignerIds);
  const family = normalizeConsentIds(values.familyMemberIds);
  const employeeInputValues = isTemplate && template
    ? cleanEmployeeInputValues(template, values.employeeInputValues)
    : {};
  return {
    residentId,
    categoryId,
    ...(roles.includes(ConsentSignerRole.EMPLOYEE) && !roleBasedEmployees ? {requiredEmployeeSignerIds: employees} : {}),
    ...(roles.includes(ConsentSignerRole.FAMILY_MEMBER) && values.familySigningMode === ConsentClinicalSigningMode.ON_SITE
      ? {familySigningMode: ConsentClinicalSigningMode.ON_SITE}
      : {}),
    ...(roles.includes(ConsentSignerRole.FAMILY_MEMBER) && values.familySigningMode !== ConsentClinicalSigningMode.ON_SITE
      ? {
        ...(values.familySigningMode ? {familySigningMode: values.familySigningMode} : {}),
        familyMemberIds: family,
      }
      : {}),
    ...(roles.includes(ConsentSignerRole.GP_CLINICAL) && values.clinicalSigningMode
      ? {
        clinicalSigningMode: values.clinicalSigningMode,
        ...(values.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER && values.clinicalSignerId
          ? {clinicalSignerId: values.clinicalSignerId}
          : {}),
      }
      : {}),
    ...(isTemplate && roles.includes(ConsentSignerRole.FAMILY_MEMBER)
      && values.familySigningMode !== ConsentClinicalSigningMode.ON_SITE
      && family.length > 1
      && family.includes(values.familyFieldCompleterId)
      ? {familyFieldCompleterId: values.familyFieldCompleterId}
      : {}),
    ...(Object.keys(employeeInputValues).length ? {employeeInputValues} : {}),
  };
};

export const validateConsentSteps = (
  category: Pick<ConsentCategory, "requiredSignerRoles" | "source" | "employeeSignerRoles"> | Pick<ConsentDocumentCategorySnapshot, "requiredSignerRoles" | "source" | "employeeSignerRoles"> | null,
  template: Pick<ConsentTemplate, "employeeInputs"> | null,
  values: ConsentStepsValues,
  forSend: boolean,
): string => {
  if (!category) return "Select a Consent Category.";
  const roleBasedEmployees = usesRoleBasedEmployeeSigners(category);
  if (category.requiredSignerRoles.includes(ConsentSignerRole.EMPLOYEE) && !roleBasedEmployees && !normalizeConsentIds(values.employeeSignerIds).length) {
    return "Select at least one employee signer.";
  }
  if (category.requiredSignerRoles.includes(ConsentSignerRole.GP_CLINICAL)) {
    if (!values.clinicalSigningMode) return "Choose how the GP / Clinician will sign.";
    if (values.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER && !values.clinicalSignerId) {
      return "Select the Brenna employee who will sign as the GP / Clinician.";
    }
    if (!roleBasedEmployees
      && values.clinicalSigningMode === ConsentClinicalSigningMode.BRENNA_USER
      && normalizeConsentIds(values.employeeSignerIds).includes(values.clinicalSignerId)) {
      return "The GP / Clinician cannot also be a required employee signer.";
    }
  }
  if (category.requiredSignerRoles.includes(ConsentSignerRole.FAMILY_MEMBER)) {
    if (!values.familySigningMode) return "Choose how the family member will sign.";
    if (values.familySigningMode === ConsentClinicalSigningMode.BRENNA_USER) {
      const family = normalizeConsentIds(values.familyMemberIds);
      if (!family.length) return "Select at least one family member signer.";
      if (category.source === ConsentCategorySource.TEMPLATE && family.length > 1
        && (!values.familyFieldCompleterId || !family.includes(values.familyFieldCompleterId))) {
        return "Select which family member completes the document fields.";
      }
    }
  }
  if (forSend && category.source === ConsentCategorySource.TEMPLATE
    && template?.employeeInputs.some((input) => {
      const value = values.employeeInputValues[input.id];
      return (input.type === "text" || input.type === "date" || input.type === "datetime") && !(typeof value === "string" && value.trim());
    })) {
    return "Complete every staff text field before sending.";
  }
  return "";
};

export const withConsentPaging = <T extends ConsentListQuery>(query: T, page: number): T & {
  page: number;
  limit: typeof CONSENT_LIST_PAGE_SIZE;
} => ({
  ...query,
  page,
  limit: CONSENT_LIST_PAGE_SIZE,
});

export interface IncrementalConsentList<T> {
  items: T[];
  page: number;
  hasMore: boolean;
}

export const createIncrementalConsentList = <T>(): IncrementalConsentList<T> => ({
  items: [],
  page: 0,
  hasMore: true,
});

export const mergeIncrementalConsentPage = <T>(
  current: IncrementalConsentList<T>,
  response: T[],
  reset = false,
): IncrementalConsentList<T> => {
  const items = reset ? response : [...current.items, ...response];
  const itemIndicesById = new Map<string, number>();
  const deduplicatedItems: T[] = [];

  items.forEach((item) => {
    const id = typeof item === "object" && item !== null && "_id" in item && typeof item._id === "string"
      ? item._id
      : undefined;

    if (!id) {
      deduplicatedItems.push(item);
      return;
    }

    const existingIndex = itemIndicesById.get(id);
    if (existingIndex === undefined) {
      itemIndicesById.set(id, deduplicatedItems.length);
      deduplicatedItems.push(item);
      return;
    }

    deduplicatedItems[existingIndex] = item;
  });

  return {
    items: deduplicatedItems,
    page: reset ? 0 : current.page + 1,
    hasMore: response.length === CONSENT_LIST_PAGE_SIZE,
  };
};
